import { ClockCircleFilled } from "@ant-design/icons";
import React, { useEffect } from "react";
import { Modal, TimePicker, message, Form } from "antd";
import dayjs from "dayjs";
import {
  useAppDispatch,
  useAppSelector,
  RootState,
} from "../../../../../store";
import { GetEmployeeAttendanceDashboardAPI } from "../../../../Redux/dashboard";
import { EditEmployeeAttendanceAPI } from "../../../../Redux/employeeAttendance";
import { BtnLoader } from "../../../../../Components/Components";
import { svg } from "../../../../../Assets/svg";
import {
  convertLocalDayjsToBusinessUTC,
  convertBusinessUTCToLocalDayjs,
  formatDateWithBusinessTZ,
} from "../../../../../helpers/moment";
import { Capacitor } from "@capacitor/core";

const ModifyAttendanceTimeModal = ({
  visible,
  onClose,
  data,
  selectedEmployee,
  dateRange,
  onSuccess: onSuccessProp,
}: any) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = React.useState(false);
  const [breakTimeError, setBreakTimeError] = React.useState<string | null>(
    null,
  );
  const { editAttendanceLoading } = useAppSelector(
    (state: RootState) => state.loading as any,
  );

  const clockInTime = Form.useWatch("clockInTime", form);
  const clockOutTime = Form.useWatch("clockOutTime", form);

  const isInvalidTime = React.useMemo(() => {
    if (!clockInTime || !clockOutTime) return false;
    const inTotalSeconds =
      clockInTime.hour() * 3600 +
      clockInTime.minute() * 60 +
      clockInTime.second();
    const outTotalSeconds =
      clockOutTime.hour() * 3600 +
      clockOutTime.minute() * 60 +
      clockOutTime.second();
    return outTotalSeconds < inTotalSeconds;
  }, [clockInTime, clockOutTime]);

  // Read-only date label — DD-MM-YYYY
  const dateLabel = data?.created_at
    ? formatDateWithBusinessTZ(data.created_at, "DD-MM-YYYY")
    : "";

  useEffect(() => {
    if (visible && data) {
      setBreakTimeError(null);
      setSubmitting(false);
      const breakMins = data.break_deduction?.deducted_minutes || 0;
      const breakHH = Math.floor(breakMins / 60)
        .toString()
        .padStart(2, "0");
      const breakMM = (breakMins % 60).toString().padStart(2, "0");
      const defaultBreakTime = dayjs(`${breakHH}:${breakMM}`, "HH:mm");

      form.setFieldsValue({
        // Time-only pickers — pre-fill from existing timestamps
        clockInTime: data.created_at
          ? convertBusinessUTCToLocalDayjs(data.created_at)
          : null,
        clockOutTime:
          data.clockOut && data.updated_at
            ? convertBusinessUTCToLocalDayjs(data.updated_at)
            : convertBusinessUTCToLocalDayjs(new Date().toISOString()),
        breakTime: defaultBreakTime,
      });
    } else {
      setBreakTimeError(null);
      setSubmitting(false);
      form.resetFields();
    }
  }, [visible, data, form]);

  const onFinish = async (values: any) => {
    if (!data?._id) return;

    // Re-combine selected time with the original date so the date doesn't change
    const originalDate = data.created_at
      ? convertBusinessUTCToLocalDayjs(data.created_at)
      : null;

    const payload: any = {};

    if (values.clockInTime && originalDate) {
      const clockInDayjs = originalDate
        .hour(values.clockInTime.hour())
        .minute(values.clockInTime.minute())
        .second(values.clockInTime.second());
      payload.clockInTime = convertLocalDayjsToBusinessUTC(clockInDayjs);
    }

    if (values.clockOutTime && originalDate) {
      let clockOutDayjs = originalDate
        .hour(values.clockOutTime.hour())
        .minute(values.clockOutTime.minute())
        .second(values.clockOutTime.second());

      // If clock-out ends up before clock-in, it crossed midnight — add a day
      if (
        values.clockInTime &&
        clockOutDayjs.isBefore(
          originalDate
            .hour(values.clockInTime.hour())
            .minute(values.clockInTime.minute())
            .second(values.clockInTime.second()),
        )
      ) {
        clockOutDayjs = clockOutDayjs.add(1, "day");
      }
      payload.clockOutTime = convertLocalDayjsToBusinessUTC(clockOutDayjs);
    }

    if (values.breakTime) {
      payload.breakTime = values.breakTime.format("HH:mm");

      // Client-side validation: break must not exceed total clocked duration
      const finalStart =
        values.clockInTime && originalDate
          ? originalDate
              .hour(values.clockInTime.hour())
              .minute(values.clockInTime.minute())
              .second(0)
          : data.created_at
          ? convertBusinessUTCToLocalDayjs(data.created_at)
          : null;
      const finalEnd =
        values.clockOutTime && originalDate
          ? (() => {
              let d = originalDate
                .hour(values.clockOutTime.hour())
                .minute(values.clockOutTime.minute())
                .second(0);
              if (
                values.clockInTime &&
                d.isBefore(
                  originalDate
                    .hour(values.clockInTime.hour())
                    .minute(values.clockInTime.minute())
                    .second(0),
                )
              ) {
                d = d.add(1, "day");
              }
              return d;
            })()
          : data.clockOut && data.updated_at
          ? convertBusinessUTCToLocalDayjs(data.updated_at)
          : null;

      if (finalStart && finalEnd) {
        const parts = payload.breakTime.split(":");
        const breakMins = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
        const totalMins = finalEnd.diff(finalStart, "minute");
        if (breakMins > totalMins) {
          setBreakTimeError(
            "Break time cannot be greater than total working hours",
          );
          return;
        }
      }
    }
    setBreakTimeError(null);

    const onSuccess = () => {
      message.success("Attendance updated successfully");
      setSubmitting(false);
      if (onSuccessProp) {
        onSuccessProp();
      } else {
        let start = "";
        let end = "";
        if (dateRange && dateRange.length === 2) {
          start =
            convertLocalDayjsToBusinessUTC(dateRange[0].startOf("day")) || "";
          end = convertLocalDayjsToBusinessUTC(dateRange[1].endOf("day")) || "";
        }
        dispatch(
          GetEmployeeAttendanceDashboardAPI({
            start,
            end,
            user: selectedEmployee,
          }),
        );
      }
      onClose();
    };

    const onError = (error: any) => {
      setSubmitting(false);
      // Error is handled globally by redux catchHandler which displays a toast
    };

    setSubmitting(true);
    dispatch(EditEmployeeAttendanceAPI(data._id, payload, onSuccess, onError));
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={576}
      closeIcon={svg?.closeIcon}
      centered
      destroyOnClose
    >
      <div className="modal-content border-0 p-0 pt-0 page-form">
        <div className="modal-header border-0 pb-1 mb-2">
          <h1
            className="modal-title fs-4 fw-bolder mx-auto text-dark"
            id="exampleModalLabel"
          >
            Modify Attendance Time
          </h1>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="modal-body pb-0"
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
        >
          {/* Read-only date — left-aligned black text */}
          {dateLabel && (
            <div className="mb-3 text-dark">
              <span className="fw-semibold">Date: </span>
              {dateLabel}
            </div>
          )}

          <div className="row">
            {data?.clockIn && (
              <div className="col-md-6">
                <div className="form-item mb-3">
                  <label className="custom-label mb-1 ff">Clock In Time</label>
                  <Form.Item name="clockInTime" className="mb-0">
                    <TimePicker
                      use12Hours
                      format="hh:mm A"
                      className="w-100"
                      size="large"
                      suffixIcon={<ClockCircleFilled />}
                      inputReadOnly={Capacitor.isNativePlatform()}
                    />
                  </Form.Item>
                </div>
              </div>
            )}

            <div className={data?.clockIn ? "col-md-6" : "col-md-12"}>
              <div className="form-item mb-3">
                <label className="custom-label mb-1 ff">Clock Out Time</label>
                <Form.Item name="clockOutTime" className="mb-0">
                  <TimePicker
                    use12Hours
                    format="hh:mm A"
                    className="w-100"
                    size="large"
                    suffixIcon={<ClockCircleFilled />}
                    inputReadOnly={Capacitor.isNativePlatform()}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-item mb-3">
                <label className="custom-label mb-1 ff">Break Time</label>
                <Form.Item
                  name="breakTime"
                  className="mb-0"
                  validateStatus={breakTimeError ? "error" : ""}
                  help={breakTimeError || undefined}
                >
                  <TimePicker
                    format="HH:mm"
                    className="w-100"
                    size="large"
                    suffixIcon={<ClockCircleFilled />}
                    placeholder="HH:MM"
                    onChange={() => setBreakTimeError(null)}
                    inputReadOnly={Capacitor.isNativePlatform()}
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          {isInvalidTime && (
            <div className="col-12 text-center text-danger mb-2 fw-bold">
              Clock Out time cannot be before Clock In time
            </div>
          )}

          <div className="d-flex justify-content-center gap-3 mt-4 mb-3">
            <button
              type="button"
              className="btn btn-outline-dark fs-6 fw-bold px-4 py-2"
              onClick={onClose}
            >
              CLOSE
            </button>
            <button
              type="submit"
              className="btn btn-dark fs-6 fw-bold px-4 py-2"
              disabled={editAttendanceLoading || isInvalidTime || submitting}
            >
              {editAttendanceLoading || submitting ? <BtnLoader /> : "UPDATE"}
            </button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default ModifyAttendanceTimeModal;
